import {
  Checkbox,
  FormControlLabel,
  Grid,
  Autocomplete,
  TextField,
  FormControl,
  FormLabel,
  Box,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import APP_CONFIG_URL_ENV from "../env-config";

const DropdownComponent = ({
  options,
  label,
  name,
  value,
  onChange,
  multiple = false,
  valueKey = "value",
  labelKey = "label",
  displayEmpty = true,
  searchable = true,
  placeholder = "",
  error = false,
  helperText,
  isNotMandatory = false,
  disabled = false,
  showCheckbox = false,
  openOnHoverEnabled = false,
  clearIcon = undefined,
  checkboxLabel = "", // Custom label for checkbox
  checkboxChecked = false, // Controlled state for checkbox
  onCheckboxChange = () => { }, // Callback for checkbox change
  ...rest
}) => {
  const [inputValue, setInputValue] = useState("");
  const [openOnHover, setOpenOnHover] = useState(false);

  // Normalize options to always be objects with keys
  const normalizedOptions = useMemo(() => {
    return options?.map(option =>
      typeof option === "object" ? option : { [valueKey]: option, [labelKey]: option }
    );
  }, [options, valueKey, labelKey]);

  const filterOptions = (opts, state) => {
    const q = (state?.inputValue || "").trim().toLowerCase();
    if (!q) return opts;
    return opts.filter(opt => String(opt?.[labelKey] ?? "").toLowerCase().includes(q));
  };

  // Handle dropdown change
  const handleChange = (event, newValue) => {
    if (multiple) {
      const nextValues = Array.isArray(newValue)
        ? newValue.map(v => v?.[valueKey]).filter(v => v !== undefined && v !== null && v !== "")
        : [];
      onChange({ target: { name, value: nextValues } });
      return;
    }

    onChange({ target: { name, value: newValue ? newValue[valueKey] : "" } });
  };

  // Handle input change for search functionality
  const handleInputChange = (event, newValue) => {
    setInputValue(newValue || "");
  };

  return (
    <FormControl
      fullWidth
      error={Boolean(error)}
      {...rest}
      onMouseLeave={() => openOnHoverEnabled && setOpenOnHover(false)}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        {/* Dropdown Label */}
        <FormLabel sx={{ display: "flex" }}>
          {label} <span className="text-error">{isNotMandatory ? "" : "*"}</span>
        </FormLabel>
        {/* Checkbox (only if showCheckbox is true) */}
        {showCheckbox && (
          <FormControlLabel
            sx={{
              justifyContent: "flex-end",
              marginRight: "0px",
              alignItems: "center",
              marginBottom: "0.5rem",
              minHeight: "auto",
              "& .MuiCheckbox-root": {
                padding: "0px",
              },
              "& .MuiFormControlLabel-label": {
                fontSize: "0.9rem",
                marginLeft: "4px",
                color: "rgba(0, 0, 0, 0.87)",
              },
            }}
            control={
              <Checkbox
                size="small"
                checked={checkboxChecked}
                onChange={e => onCheckboxChange(e.target.checked)}
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.2rem",
                  },
                }}
              />
            }
            label={checkboxLabel}
          />
        )}
      </Box>
      {/* Autocomplete Dropdown */}
      <Autocomplete
        disabled={disabled}
        multiple={multiple}
        options={normalizedOptions ? normalizedOptions : []}
        filterOptions={filterOptions}
        size="small"
        clearIcon={clearIcon}
        value={
          multiple
            ? (normalizedOptions && Array.isArray(value)
                ? normalizedOptions.filter(opt => value.includes(opt[valueKey]))
                : [])
            : (normalizedOptions
                ? normalizedOptions?.find(opt => opt[valueKey] === value) || null
                : "")
        }
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        getOptionLabel={option => option[labelKey] || ""}
        isOptionEqualToValue={(option, value) => option[valueKey] === value[valueKey]}
        getOptionDisabled={(option) => option.disabled}   
        open={openOnHoverEnabled ? openOnHover : undefined}
        openOnFocus={openOnHoverEnabled}
        onOpen={() => setOpenOnHover(true)}
        onClose={() => setOpenOnHover(false)}
        PopperProps={{
          sx: { zIndex: 2000 },        // ensures above dialog
          modifiers: [
            {
              name: "preventOverflow",
              options: {
                boundary: "viewport",
              },
            },
          ],
        }}
        renderOption={(props, option) => {
          if (option.disabled) return null;
          const { key, ...restProps } = props;
          return (
            <li key={String(option?.[valueKey] ?? key)} {...restProps}>
              {option?.flag && (
                <img
                  src={`${APP_CONFIG_URL_ENV.REACT_APP_DROPDOWN_FLAG_URL}${option.flag}`}
                  alt=""
                  style={{ width: 20, marginRight: 8 }}
                />
              )}
              {option[labelKey]}
            </li>
          );
        }}
        renderInput={params => {
          const selected = normalizedOptions
            ? normalizedOptions?.find(opt => opt[valueKey] === value)
            : "";
          return (
            <TextField
              {...params}
              variant="outlined"
              error={Boolean(error)}
              placeholder={placeholder} // <-- Added this line
              InputLabelProps={{ shrink: true }}
              helperText={error}
              onMouseEnter={() => openOnHoverEnabled && setOpenOnHover(true)}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {!multiple && selected?.flag && (
                      <img
                        src={`${APP_CONFIG_URL_ENV.REACT_APP_DROPDOWN_FLAG_URL}${selected.flag}`}
                        alt=""
                        width={20}
                        style={{ marginLeft: 4 }}
                      />
                    )}
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          );
        }}
      />
      {helperText && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default DropdownComponent;
